import {createFeatureSelector, createSelector} from '@ngrx/store';
import {RouterReducerState} from '@ngrx/router-store';
import {convertToParamMap, Data, ParamMap, Params} from '@angular/router';
import {MxSelector, MxSelectorCreator} from '@aviary/store/store.types';
import {INITIAL_SERIALIZED_ROUTER_STATE, MxBreadcrumbs, ROUTER_FEATURE_KEY, SerializedRouterState} from '@aviary/store/router/router.types';
import {SelectOptions} from '@aviary/store/common/common.types';
import {isDefined} from '@aviary/store/common/typing.helpers';
import {kebabCaseToSentenceCase} from '@aviary/store/transformation.helpers';
import {ExpressionNode} from '@rsql/ast';
import {parse} from '@rsql/parser';

// For each app to call in an adapter file, using its own RouteName type and router constants.
export function createRouterSelectors<RouteNameType extends string = string>(
  getChildRouteNamesFor?: (routeName: RouteNameType) => RouteNameType[] | undefined,
  getRouterTabIndexFor?: (routeName: RouteNameType) => number | undefined
) {
  const entireState: MxSelector<RouterReducerState<SerializedRouterState<RouteNameType>>> =
    createFeatureSelector<RouterReducerState<SerializedRouterState<RouteNameType>>>(ROUTER_FEATURE_KEY);

  const serializedState: MxSelector<SerializedRouterState<RouteNameType>> = createSelector(
    entireState,
    state => state?.state ?? INITIAL_SERIALIZED_ROUTER_STATE
  );

  const url: MxSelector<string> = createSelector(
    serializedState,
    state => state.url
  );

  const path: MxSelector<string> = createSelector(
    serializedState,
    state => state.path
  );

  const breadcrumbs: MxSelector<MxBreadcrumbs<RouteNameType>> = createSelector(
    serializedState,
    state => state.breadcrumbs
  );

  const fragment: MxSelector<string> = createSelector(
    serializedState,
    state => state.fragment
  );

  const params: MxSelector<Params> = createSelector(
    serializedState,
    state => state.params
  );

  const paramsMap: MxSelector<ParamMap> = createSelector(
    params,
    _params => convertToParamMap(_params)
  );

  const paramValue: MxSelectorCreator<[string], string | undefined> =
    paramName => createSelector(
      paramsMap,
      p => p.get(paramName) ?? undefined
    );

  const paramValueWithFallback: MxSelectorCreator<[string, string], string> =
    (paramName, fallbackValue) => createSelector(
      paramValue(paramName),
      value => value ?? fallbackValue
    );

  const paramValueAsNumber: MxSelectorCreator<[string], number | undefined> =
    paramName => createSelector(
      paramValue(paramName),
      value => {
        const number = Number(value);
        return Number.isNaN(number) ? undefined : number;
      }
    );

  const paramValueAsNumberWithFallback: MxSelectorCreator<[string, number], number> =
    (paramName, fallbackValue) => createSelector(
      paramValueAsNumber(paramName),
      value => value ?? fallbackValue
    );

  const queryParams: MxSelector<Params> = createSelector(
    serializedState,
    state => state.queryParams
  );

  const queryParamNames: MxSelector<string[]> = createSelector(
    queryParams,
    _params => Object.keys(_params)
  );

  const queryParamsMap: MxSelector<ParamMap> = createSelector(
    queryParams,
    _params => convertToParamMap(_params)
  );

  const queryParamValue: MxSelectorCreator<[string], string | undefined> =
    paramName => createSelector(
      queryParamsMap,
      p => p.get(paramName) ?? undefined
    );

  const hasQueryParamValue: MxSelectorCreator<[string], boolean> =
    paramName => createSelector(
      queryParamValue(paramName),
      isDefined
    );

  const queryParamValueAsSelectOptions: MxSelectorCreator<[string, MxSelector<SelectOptions<string>>], SelectOptions<string>> =
    (paramName, selectOptionsSelector) => createSelector(
      queryParamValue(paramName),
      selectOptionsSelector,
      (value, selectOptions) => {
        if (!value) return [];

        const queryValues: string[] = value.split(',');

        return selectOptions.filter(option =>
          queryValues.some(queryValue => option.value === decodeURIComponent(queryValue))
        );
      }
    );

  const queryParamValueAsDisplayValue: MxSelectorCreator<[string, MxSelector<SelectOptions<string>>], string> =
    (paramName, selectOptionsSelector) => createSelector(
      queryParamValueAsSelectOptions(paramName, selectOptionsSelector),
      selectOptions => {
        if (selectOptions.length === 0) return kebabCaseToSentenceCase(paramName);

        const firstDisplayName: string = selectOptions[0].displayName;
        const additionalCount: string =
          selectOptions.length > 1
            ? ` +${selectOptions.length - 1}`
            : '';

        return `${firstDisplayName}${additionalCount}`;
      }
    );

  const queryParamValueWithFallback: MxSelectorCreator<[string, string], string> =
    (paramName, fallbackValue) => createSelector(
      queryParamValue(paramName),
      value => value ?? fallbackValue
    );

  const queryParamValueAsNumber: MxSelectorCreator<[string], number | undefined> =
    paramName => createSelector(
      queryParamValue(paramName),
      value => {
        const number = Number(value);
        return Number.isNaN(number) ? undefined : number;
      }
    );

  const queryParamValueAsNumberWithFallback: MxSelectorCreator<[string, number], number> =
    (paramName, fallbackValue) => createSelector(
      queryParamValueAsNumber(paramName),
      value => value ?? fallbackValue
    );

  const queryParamValueAsRsqlExpressionNode: MxSelectorCreator<[string], ExpressionNode | undefined> =
    paramName => createSelector(
      queryParamValue(paramName),
      value => {
        if (!value) return undefined;

        try {
          return parse(value);
        } catch {
          return undefined;
        }
      }
    );

  const isParseableRsqlValue: MxSelectorCreator<[string], boolean> =
    paramName => createSelector(
      queryParamValueAsRsqlExpressionNode(paramName),
      isDefined
    );

  const data: MxSelector<Data> = createSelector(
    serializedState,
    state => state.data
  );

  const routeName: MxSelector<RouteNameType | undefined> = createSelector(
    data,
    _data => _data['name'] as RouteNameType | undefined
  );

  const dataValue: MxSelectorCreator<[string], string | undefined> =
    key => createSelector(
      data,
      _data => _data[key]
    );

  const initialQueryParams: MxSelector<Params | undefined> = createSelector(
    data,
    ({initialQueryParams}) => initialQueryParams
  );

  const routeNameMatches: MxSelectorCreator<[RouteNameType, ...RouteNameType[]], boolean> =
    (...targetRouteNames) => createSelector(
      routeName,
      currentRouteName => !!currentRouteName && targetRouteNames.includes(currentRouteName)
    );

  const routeNameMatchesAChildOf: MxSelectorCreator<[RouteNameType, ...RouteNameType[]], boolean> =
    (...targetRouteNames) => createSelector(
      routeName,
      currentRouteName => {
        if (!currentRouteName || !getChildRouteNamesFor) return false;

        return targetRouteNames.some(targetRouteName => {
          const children: RouteNameType[] = getChildRouteNamesFor(targetRouteName) ?? [];

          return children.includes(currentRouteName);
        });
      }
    );

  const routeNameMatchesADescendantOf: MxSelectorCreator<[RouteNameType, ...RouteNameType[]], boolean> =
    (...targetRouteNames) => createSelector(
      routeName,
      currentRouteName => {
        if (!currentRouteName || !getChildRouteNamesFor) return false;

        const isDescendantOf = (ancestorRouteName: RouteNameType, candidateRouteName: RouteNameType, visitedRouteNames = new Set<RouteNameType>()): boolean => {
          if (visitedRouteNames.has(ancestorRouteName)) return false;
          visitedRouteNames.add(ancestorRouteName);

          const children: RouteNameType[] = getChildRouteNamesFor(ancestorRouteName) ?? [];
          if (children.includes(candidateRouteName)) return true;

          return children.some(child => isDescendantOf(child, candidateRouteName, visitedRouteNames));
        };

        return targetRouteNames.some(targetRouteName => isDescendantOf(targetRouteName, currentRouteName));
      }
    );

  const routeNameMatchesIncludingChildren: MxSelectorCreator<[RouteNameType, ...RouteNameType[]], boolean> =
    (...targetRouteNames) => createSelector(
      routeNameMatches(...targetRouteNames),
      routeNameMatchesAChildOf(...targetRouteNames),
      (isTarget, isChildOfTarget) => isTarget || isChildOfTarget
    );

  const tabIndex: MxSelector<number | undefined> = createSelector(
    routeName,
    _routeName => isDefined(_routeName) && isDefined(getRouterTabIndexFor) ? getRouterTabIndexFor(_routeName) : undefined
  );

  return {
    entireState,
    serializedState,
    url,
    path,
    breadcrumbs,
    fragment,
    params,
    paramsMap,
    paramValue,
    paramValueWithFallback,
    paramValueAsNumber,
    paramValueAsNumberWithFallback,
    queryParams,
    queryParamNames,
    queryParamsMap,
    queryParamValue,
    hasQueryParamValue,
    queryParamValueAsSelectOptions,
    queryParamValueAsDisplayValue,
    queryParamValueWithFallback,
    queryParamValueAsNumber,
    queryParamValueAsNumberWithFallback,
    queryParamValueAsRsqlExpressionNode,
    isParseableRsqlValue,
    data,
    routeName,
    dataValue,
    initialQueryParams,
    routeNameMatches,
    routeNameMatchesAChildOf,
    routeNameMatchesADescendantOf,
    routeNameMatchesIncludingChildren,
    tabIndex
  } as const;
}

// For Aviary use only.
export const RouterSelectors = createRouterSelectors<string>();
